import { useEffect, useRef, useState } from "react";

import {
  Anchor,
  IScalableWrapper,
  MaintainAspectRatioBy,
} from "./ScalableWrapper.types";
import {
  getSafeAreaInset,
  getWindowInnerHeight,
  getWindowInnerWidth,
} from "@utils/helpers";
import View from "@components/View";

import "./ScalableWrapper.css";

const scalingRatios = {
  "16_9": {
    width: 1280,
    height: 720,
    widthRatio: 16,
    heightRatio: 9,
    mWidth: 720,
    mHeight: 1280,
    mWidthRatio: 9,
    mHeightRatio: 16,
  },
  "4_3": {
    width: 1024,
    height: 768,
    widthRatio: 4,
    heightRatio: 3,
    mWidth: 768,
    mHeight: 1024,
    mWidthRatio: 3,
    mHeightRatio: 4,
  },
};

const ScalableWrapper = (props: IScalableWrapper) => {
  const {
    children,
    backgroundNode,
    ratio = "16_9",
    containerStyle,
    parentContainerStyle,
    anchor,
    autoAdjustAspectRatio = false,
    lockOrientation = 'landscape',
    maintainAspectRatioBy = "width",
    disabled,
    safeAreaInsets = {
      top: "discord",
      bottom: "discord",
      left: "discord",
      right: "discord",
    },
  } = props;

  const isScalingEnabledRef = useRef<boolean>(!disabled);

  const parentContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [containerAnchor, setContainerAnchor] = useState<Anchor | null>();

  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
    widthRatio: number;
    heightRatio: number;
    isPortraitMode: boolean;
  }>(getDimensions());
  const { width, height, widthRatio, heightRatio } = dimensions;

  useEffect(() => {
    function calculateDimensions() {
      const dimensions = getDimensions();
      setDimensions(dimensions);
    }

    calculateDimensions();

    window.addEventListener("resize", calculateDimensions);

    return () => window.removeEventListener("resize", calculateDimensions);
  }, [ratio]);

  useEffect(() => {
    scaleUI();

    window.addEventListener("resize", scaleUI);

    return () => {
      window.removeEventListener("resize", scaleUI);
    };
  }, [dimensions, anchor, maintainAspectRatioBy]);

  function getDimensions() {
    let width = scalingRatios[ratio].mWidth;
    let height = scalingRatios[ratio].mHeight;
    let widthRatio = scalingRatios[ratio].mWidthRatio;
    let heightRatio = scalingRatios[ratio].mHeightRatio;
    let isPortraitMode = true;

    if (lockOrientation === "landscape") {
      width = scalingRatios[ratio].width;
      height = scalingRatios[ratio].height;
      widthRatio = scalingRatios[ratio].widthRatio;
      heightRatio = scalingRatios[ratio].heightRatio;
      isPortraitMode = false;
    } else if (lockOrientation === "portrait") {
      width = scalingRatios[ratio].mWidth;
      height = scalingRatios[ratio].mHeight;
      widthRatio = scalingRatios[ratio].mWidthRatio;
      heightRatio = scalingRatios[ratio].mHeightRatio;
      isPortraitMode = true;
    } else if (
      getWindowInnerWidth(safeAreaInsets) > getWindowInnerHeight(safeAreaInsets)
    ) {
      width = scalingRatios[ratio].width;
      height = scalingRatios[ratio].height;
      widthRatio = scalingRatios[ratio].widthRatio;
      heightRatio = scalingRatios[ratio].heightRatio;
      isPortraitMode = false;
    }

    return {
      width,
      height,
      widthRatio,
      heightRatio,
      isPortraitMode,
    };
  }

  function scaleUI() {
    const container = containerRef.current;
    const parentContainer = parentContainerRef.current;

    if (container && parentContainer && isScalingEnabledRef.current) {
      let newWidth = width;
      let newHeight = height;

      if (typeof maintainAspectRatioBy !== "string") {
        const aspectRatio =
          getWindowInnerWidth(safeAreaInsets) /
          getWindowInnerHeight(safeAreaInsets);

        const scaledDimensions = getScaledWidthAndHeight(
          aspectRatio >= maintainAspectRatioBy.breakpointByAspectRatio
            ? maintainAspectRatioBy.ratioBy[1]
            : maintainAspectRatioBy.ratioBy[0]
        );
        newWidth = scaledDimensions.newWidth;
        newHeight = scaledDimensions.newHeight;
      } else {
        const scaledDimensions = getScaledWidthAndHeight(maintainAspectRatioBy);
        newWidth = scaledDimensions.newWidth;
        newHeight = scaledDimensions.newHeight;
      }

      container.style.transform = `scale(${newWidth / width}, ${
        newHeight / height
      })`;
      container.style.opacity = "1";

      if (anchor) {
        flushAnchors();
        anchorUI({ width: newWidth, height: newHeight, anchor });
        setContainerAnchor(anchor);
      } else if (containerAnchor) {
        // to remove the previously applied anchor
        flushAnchors();
        setContainerAnchor(null);
      }
    }
  }

  function getScaledWidthAndHeight(
    maintainAspectRatioBy: MaintainAspectRatioBy
  ) {
    const windowWidth = getWindowInnerWidth(safeAreaInsets);
    const windowHeight = getWindowInnerHeight(safeAreaInsets);

    let newWidth = windowWidth;
    let newHeight = windowHeight;

    if (maintainAspectRatioBy === "width") {
      newWidth = windowWidth;
      newHeight = (newWidth * heightRatio) / widthRatio;

      if (autoAdjustAspectRatio && newHeight > windowHeight) {
        newHeight = windowHeight;
        newWidth = (newHeight * widthRatio) / heightRatio;
      }
    } else if (maintainAspectRatioBy === "height") {
      newHeight = windowHeight;
      newWidth = (newHeight * widthRatio) / heightRatio;

      if (autoAdjustAspectRatio && newWidth > windowWidth) {
        newWidth = windowWidth;
        newHeight = (newWidth * heightRatio) / widthRatio;
      }
    } else if (maintainAspectRatioBy === "both") {
      newWidth = windowWidth;
      newHeight = windowHeight;
    }

    return { newWidth, newHeight };
  }

  function flushAnchors() {
    const parentContainer = parentContainerRef.current;

    if (parentContainer) {
      parentContainer.style.top = "initial";
      parentContainer.style.bottom = "initial";
      parentContainer.style.left = "initial";
      parentContainer.style.right = "initial";
    }
  }

  function anchorUI(config: { width: number; height: number; anchor: Anchor }) {
    const { width, height, anchor } = config;
    const parentContainer = parentContainerRef.current;

    if (!parentContainer) {
      return;
    }

    if (anchor === "top" || anchor === "bottom") {
      const marginVertical =
        parentContainer.getBoundingClientRect().height -
        (getSafeAreaInset("t", safeAreaInsets.top) +
          getSafeAreaInset("b", safeAreaInsets.bottom)) -
        height;
      parentContainer.style[anchor] = `${(marginVertical / 2) * -1}px`;
      return;
    }

    if (anchor === "left" || anchor === "right") {
      const marginHorizontal =
        parentContainer.getBoundingClientRect().width -
        (getSafeAreaInset("l", safeAreaInsets.left) +
          getSafeAreaInset("r", safeAreaInsets.right)) -
        width;
      parentContainer.style[anchor] = `${(marginHorizontal / 2) * -1}px`;
      return;
    }

    if (
      anchor === "top-left" ||
      anchor === "top-right" ||
      anchor === "bottom-left" ||
      anchor === "bottom-right"
    ) {
      const anchors = anchor.split("-") as Anchor[];

      // apply anchor
      anchorUI({ ...config, anchor: anchors[0] });
      anchorUI({ ...config, anchor: anchors[1] });
      return;
    }
  }

  if (disabled) {
    return children;
  }

  return (
    <>
      {backgroundNode && (
        <View className="sw_background-node">{backgroundNode}</View>
      )}
      <View
        ref={parentContainerRef}
        className="sw_parent-container"
        style={{
          ...parentContainerStyle,
          width: "100vw",
          height: "100%",
        }}
      >
        <View
          ref={containerRef}
          className="sw_container"
          style={{
            ...containerStyle,
            width,
            height,
          }}
        >
          {children}
        </View>
      </View>
    </>
  );
};

export default ScalableWrapper;
